import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';

/*
 * The editable prediction thresholds (ITP-E-142-08): one definition per value — its label, the sentence it
 * completes, its unit and its range — from the approved Carbon prototype (OCA-E-159 settingsFields.ts). Ranges are
 * in the units the editor shows (percent as whole percent); ITP-API checks the same ranges (InsightSettingsRules).
 */

export type SettingUnit = 'days' | 'pct' | 'count' | 'times' | 'usd';
export type ThresholdKey = 'renewalWindowDays' | 'renewalLoginRatio' | 'lowSeatUse' | 'highUnresolved'
  | 'darkRatio' | 'darkMinWeekly' | 'spikeMultiplier' | 'spikeMinUsd' | 'usageDropRatio' | 'usageMinProjects';

export interface ThresholdField {
  key: ThresholdKey;
  label: string;
  before: string;
  after: string;
  unit: SettingUnit;
  min: number;
  max: number;
  step: number;
  whole: boolean;
  addition?: string;
}

export interface ThresholdGroup {
  id: string;
  title: string;
  rule: string;
  fields: ThresholdField[];
}

export const THRESHOLD_GROUPS: ThresholdGroup[] = [
  {
    id: 'renewal',
    title: 'Renewal risk',
    rule: 'A contract that ends soon while engagement falls.',
    fields: [
      {key: 'renewalWindowDays', label: 'Renewal window', before: 'Contract or licence ends within', after: 'days', unit: 'days', min: 7, max: 365, step: 1, whole: true},
      {key: 'renewalLoginRatio', label: 'Engagement drop', before: 'and 4-week logins are below', after: 'of the 12-week average', unit: 'pct', min: 10, max: 95, step: 1, whole: true},
      {key: 'lowSeatUse', label: 'Low seat use', before: 'Adds to the score: seat use below', after: '', unit: 'pct', min: 5, max: 95, step: 1, whole: true},
      {key: 'highUnresolved', label: 'High unresolved rate', before: 'Adds to the score: XatBot unresolved above', after: '', unit: 'pct', min: 5, max: 95, step: 1, whole: true}
    ]
  },
  {
    id: 'dark',
    title: 'Going dark',
    rule: "A company that usually logs in, suddenly doesn't.",
    fields: [
      {key: 'darkRatio', label: 'Activity floor', before: 'Logins in the last 14 days are below', after: "of the company's normal fortnight", unit: 'pct', min: 5, max: 90, step: 1, whole: true},
      {key: 'darkMinWeekly', label: 'Qualifying activity', before: 'Only companies averaging at least', after: 'logins a week over 8 weeks', unit: 'count', min: 1, max: 500, step: 1, whole: true}
    ]
  },
  {
    id: 'spike',
    title: 'XatBot spend spike',
    rule: "A day of XatBot spend far above the company's own normal.",
    fields: [
      {key: 'spikeMultiplier', label: 'Spike multiple', before: 'A day above', after: "the company's 14-day median", unit: 'times', min: 1.5, max: 20, step: 0.1, whole: false},
      {key: 'spikeMinUsd', label: 'Minimum spike', before: 'and at least', after: 'on the day', unit: 'usd', min: 0, max: 10000, step: 5, whole: false,
       addition: 'Without a floor, a company going from one question a day to four reads as a 4× spike worth a dollar.'}
    ]
  },
  {
    // ITP-E-216-05. Usage falling (Plan ITP-P-50 Q17); ITP-API checks the same ranges (InsightSettingsRules, ITP-E-216-02).
    id: 'usage',
    title: 'Usage falling',
    rule: 'A company that usually opens projects, suddenly opens far fewer.',
    fields: [
      {key: 'usageDropRatio', label: 'Usage drop', before: 'Projects opened in the last 4 weeks are below', after: 'of the 12-week average', unit: 'pct', min: 5, max: 95, step: 1, whole: true},
      {key: 'usageMinProjects', label: 'Minimum usual projects', before: 'Only companies usually opening at least', after: 'projects a week over 12 weeks', unit: 'count', min: 1, max: 500, step: 1, whole: true}
    ]
  }
];

/** The value as the editor shows it: percentages as whole numbers (0.6 → 60). */
export function toInput(unit: SettingUnit, value: number): number {
  return unit === 'pct' ? Math.round(value * 100) : value;
}

/** The stored value of an editor input: whole percent back to a ratio (60 → 0.6). */
export function fromInput(unit: SettingUnit, value: number): number {
  return unit === 'pct' ? value / 100 : value;
}

/** A blank or whole number passes; anything else is `{whole: true}`. */
export const wholeNumber: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  control.value === null || control.value === '' || Number.isInteger(Number(control.value)) ? null : {whole: true};

export function rangeValidators(min: number, max: number, whole: boolean): ValidatorFn[] {
  const validators: ValidatorFn[] = [Validators.required, Validators.min(min), Validators.max(max)];
  return whole ? [...validators, wholeNumber] : validators;
}

/** The message under a field: "Enter a value." / "Between 7 and 365." / "Whole numbers only.", or null when valid. */
export function fieldError(control: AbstractControl, min: number, max: number): string | null {
  const errors = control.errors;
  if (!errors) {
    return null;
  }
  if (errors['required']) {
    return 'Enter a value.';
  }
  if (errors['min'] || errors['max']) {
    return `Between ${min} and ${max}.`;
  }
  if (errors['whole']) {
    return 'Whole numbers only.';
  }
  return null;
}

/** One control per threshold, keyed by its settings property name. */
export function buildThresholdsGroup(): FormGroup {
  const controls: {[key: string]: AbstractControl} = {};
  THRESHOLD_GROUPS.forEach(group => group.fields.forEach(field =>
    controls[field.key] = new FormControl<number | null>(null, rangeValidators(field.min, field.max, field.whole))));
  return new FormGroup(controls);
}

export type WeightKey = 'weightEngagement' | 'weightSeats' | 'weightRenewal' | 'weightQuality';

export interface WeightField {
  key: WeightKey;
  label: string;
  detail: string;
}

/* The health-score weights (ITP-E-142-09), from the Carbon prototype's WEIGHT_FIELDS. Whole percent, 0–100 each. */
export const WEIGHT_FIELDS: WeightField[] = [
  {key: 'weightEngagement', label: 'Engagement trend', detail: 'Logins over 4 weeks against 12 weeks'},
  {key: 'weightSeats', label: 'Seat utilisation', detail: 'Active users in 30 days against licensed seats'},
  {key: 'weightRenewal', label: 'Renewal proximity × trend', detail: 'How close the renewal is, scaled by the engagement drop'},
  {key: 'weightQuality', label: 'XatBot quality', detail: 'Unresolved rate and negative feedback'}
];

/** The four weights added up; a blank weight counts as 0. */
export function weightTotal(values: {[key: string]: number | null}): number {
  return WEIGHT_FIELDS.reduce((sum, field) => sum + (Number(values?.[field.key]) || 0), 0);
}

/** The weights group is valid only when the four add up to exactly 100; otherwise `{total: {total}}`. */
export const weightsTotalValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const total = weightTotal((group as FormGroup).getRawValue());
  return total === 100 ? null : {total: {total}};
};

/** One control per weight (whole 0–100), with the must-total-100 group validator. */
export function buildWeightsGroup(): FormGroup {
  const controls: {[key: string]: AbstractControl} = {};
  WEIGHT_FIELDS.forEach(field => controls[field.key] = new FormControl<number | null>(null, rangeValidators(0, 100, true)));
  return new FormGroup(controls, {validators: weightsTotalValidator});
}

/**
 * ITP-E-156-02. True when an editor value differs from the saved one (both in the editor's units); a blank value
 * differs; nothing saved yet (`saved` null) never differs.
 */
export function differsFromSaved(value: unknown, saved: number | null): boolean {
  if (saved === null) {
    return false;
  }
  if (value === null || value === undefined || value === '') {
    return true;
  }
  return Number(value) !== saved;
}

/**
 * A value in the editor's units, worded like the server's Last change line (InsightSettingsRules.Format):
 * "30%", "90 days", "3×", "$25", "5"; a blank value is "—".
 */
export function formatInput(unit: SettingUnit, value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  switch (unit) {
    case 'pct':
      return `${value}%`;
    case 'days':
      return `${value} days`;
    case 'times':
      return `${value}×`;
    case 'usd':
      return `$${value}`;
    default:
      return `${value}`;
  }
}
